import { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';

export interface Category {
  id: string;
  name: string;
}

export interface Product {
  id: number;
  name: string;
  description: string;
  price: number;
  image_url?: string;
  has_options: boolean;
  main_category_id: string;
  category_id: string;
  options?: {
    size?: 'normal' | 'double';
    strength?: 'light' | 'strong';
  };
}

export interface HomeDataState {
  // Categories
  mainCategories: Category[];
  subCategories: Category[];
  selectedMainCategory: string;
  selectedSubCategory: string;

  // Products
  products: Product[];
  loading: boolean;
  error: string | null;

  // UI State
  showSubFilter: boolean;
}

export interface HomeDataActions {
  setSelectedMainCategory: (category: string) => void;
  setSelectedSubCategory: (category: string) => void;
}

export const useHomeData = (): HomeDataState & HomeDataActions => {
  const [selectedMainCategory, setSelectedMainCategory] = useState<string>('all');
  const [selectedSubCategory, setSelectedSubCategory] = useState<string>('all');
  const [showSubFilter, setShowSubFilter] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mainCategories, setMainCategories] = useState<Category[]>([]);
  const [subCategories, setSubCategories] = useState<Category[]>([]);
  const [allCategories, setAllCategories] = useState<any[]>([]);

  // Fetch all categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/categories`);
        setAllCategories(response.data);

        const mainCats = [
          { id: 'all', name: 'Tümü' },
          ...response.data
            .filter((cat: any) => cat.parent_id === null)
            .map((cat: any) => ({
              id: String(cat.id),
              name: cat.name,
            })),
        ];
        setMainCategories(mainCats);
      } catch (error) {
        console.error('Categories fetch error:', error);
        setMainCategories([{ id: 'all', name: 'Tümü' }]);
      }
    };

    fetchCategories();
  }, []);

  // Handle sub-filter visibility
  useEffect(() => {
    if (selectedMainCategory === 'all') {
      setTimeout(() => setShowSubFilter(false), 250);
    } else {
      setShowSubFilter(true);
    }
  }, [selectedMainCategory]);

  // Fetch products based on selected categories
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      setError(null);

      try {
        // If both categories are 'all', fetch all products
        if (selectedMainCategory === 'all' && selectedSubCategory === 'all') {
          const response = await axios.get(`${API_BASE_URL}/api/products`);
          setProducts(response.data);
        } else {
          // Use filter endpoint for specific categories
          const params = new URLSearchParams();

          if (selectedMainCategory !== 'all') {
            params.append('main_category_id', selectedMainCategory);
          }
          if (selectedSubCategory !== 'all') {
            params.append('category_id', selectedSubCategory);
          }

          const response = await axios.get(
            `${API_BASE_URL}/api/products/filter?${params.toString()}`
          );
          setProducts(response.data);
        }
      } catch (error) {
        console.error('Products fetch error:', error);
        setError('Ürünler yüklenemedi');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [selectedMainCategory, selectedSubCategory]);

  // Update sub-categories based on selected main category
  useEffect(() => {
    if (selectedMainCategory !== 'all') {
      const parent = allCategories.find(cat => String(cat.id) === selectedMainCategory);
      if (parent) {
        const subCats = [
          { id: 'all', name: 'Tümü' },
          ...allCategories
            .filter((cat: any) => cat.parent_name === parent.name)
            .map((cat: any) => ({
              id: String(cat.id),
              name: cat.name,
            })),
        ];
        setSubCategories(subCats);
      } else {
        setSubCategories([{ id: 'all', name: 'Tümü' }]);
      }
    } else {
      setSubCategories([]);
    }
  }, [selectedMainCategory, allCategories]);

  const handleMainCategoryChange = (category: string) => {
    setSelectedMainCategory(category);
    setSelectedSubCategory('all');
  };

  return {
    // State
    mainCategories,
    subCategories,
    selectedMainCategory,
    selectedSubCategory,
    products,
    loading,
    error,
    showSubFilter,

    // Actions
    setSelectedMainCategory: handleMainCategoryChange,
    setSelectedSubCategory,
  };
};
